import React, {useEffect, useState} from 'react';
import {Image, StyleSheet, View} from 'react-native';

import {initials} from '@/shared/lib/format';
import {fonts} from '@/shared/ui/fonts';
import {StatusDot, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {useTheme} from '@/shared/ui/theme';

/**
 * A person's face, or their initials when there is no face to show.
 *
 * The backend returns a bare base64 payload (no `data:` prefix) from fetchPersonnel,
 * so we wrap it into a data URI here. Denied events and unrecognised faces have no
 * record and thus no avatar — the caller passes only `name` and the initials disc
 * stands in. That disc uses the muted wash, never a status colour: an avatar is an
 * identity, not a verdict, and must not be mistaken for a granted/denied signal.
 *
 * `badgeTone` is the exception that proves the rule — a small dot notched into the
 * bottom-right corner, cut out of the surface colour so it reads as attached to the
 * avatar rather than floating over it. That is where a verdict may live, because it is
 * unmistakably a separate mark from the face.
 *
 * `brand` is the other exception, and it applies to exactly ONE person: whoever is
 * signed in. That disc is not a row in a list — it is the answer to "who am I here" —
 * so it takes the brand red rather than the inset grey every other avatar sits on.
 * Never pass it for a personnel record.
 */
export type AvatarProps = {
  name: string;
  /** Raw base64 (as fetchPersonnel returns it), or null/absent for initials only. */
  base64?: string | null;
  /** A remote image URL (e.g. the gateway avatar endpoint). Takes precedence over base64. */
  uri?: string | null;
  /** Headers sent with `uri` (auth). Ignored without `uri`. */
  headers?: Record<string, string>;
  size?: number;
  /** A status dot notched into the corner — "arrived today", "online". */
  badgeTone?: StatusTone | null;
  /** The signed-in operator's own disc: initials on the brand gradient. */
  brand?: boolean;
};

// PNG magic base64-encodes to a leading "iVBOR"; anything else the terminal sends is
// JPEG. RN's decoders sniff the bytes anyway, but a correct mime avoids surprises.
function dataUri(base64: string): string {
  const mime = base64.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';
  return `data:${mime};base64,${base64}`;
}

const styles = StyleSheet.create({
  initials: {alignItems: 'center', justifyContent: 'center'},
  // Semibold via the family, never fontWeight — see fonts.ts.
  initialsText: {fontFamily: fonts.sans.semibold},
  badge: {
    position: 'absolute',
    right: -1,
    bottom: -1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
  },
});

export function Avatar({
  name,
  base64,
  uri,
  headers,
  size = 44,
  badgeTone,
  brand = false,
}: AvatarProps) {
  const p = useTheme();
  const box = {width: size, height: size, borderRadius: size / 2};
  const [errored, setErrored] = useState(false);

  // A new uri (avatar changed → ?v=revision bumped) gets a fresh chance to load.
  useEffect(() => setErrored(false), [uri]);

  let face: React.ReactNode;

  if (uri && !errored) {
    face = (
      <Image
        testID="avatar-image"
        source={{uri, headers}}
        onError={() => setErrored(true)}
        style={[box, {backgroundColor: p.inset}]}
      />
    );
  } else if (base64) {
    face = (
      <Image
        testID="avatar-image"
        source={{uri: dataUri(base64)}}
        style={[box, {backgroundColor: p.inset}]}
      />
    );
  } else {
    face = (
      <View
        testID="avatar-initials"
        style={[
          styles.initials,
          box,
          brand
            ? {backgroundColor: p.primary}
            : {backgroundColor: p.inset, borderWidth: StyleSheet.hairlineWidth, borderColor: p.borderSubtle},
        ]}>
        <AppText
          color={brand ? p.primaryForeground : p.textSecondary}
          style={[styles.initialsText, {fontSize: Math.round(size * 0.36)}]}>
          {initials(name)}
        </AppText>
      </View>
    );
  }

  if (!badgeTone) {
    return <>{face}</>;
  }

  const ring = Math.max(14, Math.round(size * 0.3));
  return (
    <View style={box}>
      {face}
      <View
        style={[
          styles.badge,
          {width: ring, height: ring, backgroundColor: p.card},
        ]}>
        <StatusDot tone={badgeTone} size={Math.round(ring * 0.62)} />
      </View>
    </View>
  );
}
